import { useState, useEffect, useMemo, SyntheticEvent } from 'react';
import {
  Typography,
  Divider,
  Grid,
  Container,
  Paper,
  Box,
  CircularProgress,
  Card,
  CardContent,
  Tab,
  Tabs
} from '@mui/material';
import { formatDateToDayMonthYearTime } from '@/utils/dateFormatters';
import { Helmet } from 'react-helmet-async';
import PageTitleWrapper from '@/components/PageTitleWrapper';
import { useTranslation } from 'react-i18next';
import {
  Link as RouterLink,
  useParams,
  useLocation,
  useSearchParams
} from 'react-router-dom';
import { Link } from '@mui/material';
import { branchService } from '@/data/branchService';
import { Branch } from '@/types/Branch.interface';
import { BranchStatus } from '@/enums/branch-status';
import { FeatureKey } from '@/types/Licensing.interface';
import StatusLabel from '@/components/StatusLabel';
import ShoppingCartIcon from '@mui/icons-material/ShoppingCart';
import PersonIcon from '@mui/icons-material/Person';
import LocalOfferIcon from '@mui/icons-material/LocalOffer';
import EmailIcon from '@mui/icons-material/Email';
import PhoneIcon from '@mui/icons-material/Phone';
import MapIcon from '@mui/icons-material/Map';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import QrCode2Icon from '@mui/icons-material/QrCode2';
import EventAvailableIcon from '@mui/icons-material/EventAvailable';
import AccountBalanceIcon from '@mui/icons-material/AccountBalance';
import PointOfSaleIcon from '@mui/icons-material/PointOfSale';
import { branchState$, setSelectedBranch } from '@/store/branchStore';
import { useObservable } from '@legendapp/state/react';
import ImagePreviewModal from '@/components/images/ImagePreviewModal';
import BranchFinancialInfoForm from '@/components/branch/BranchFinancialInfoForm';
import BranchTaxIdentityCard from '@/components/branch/BranchTaxIdentityCard';
import { useUserViewMode } from '@/hooks/useUserViewMode';
import { hasTaxSetupSupport } from '@/utils/b2bTaxErrors';
import DailyLoginTypesManager from '@/components/branch/DailyLoginTypesManager';
import BranchPosSettingsManager from '@/components/branch/BranchPosSettingsManager';
import BranchPosTransactionsManager from '@/components/branch/BranchPosTransactionsManager';
import BranchFeatureGate from '@/components/branch/BranchFeatureGate';
import BranchQrMenuManager from '@/components/branch/BranchQrMenuManager';
import BranchBalanceSettingsCard from '@/components/branch/BranchBalanceSettingsCard';
import PageHeader from '@/content/Management/Branches/DetailsPageHeader';
import TabPanel from '@/components/TabPanel';

type BranchTabKey =
  | 'overview'
  | 'qr-menu'
  | 'daily-logins'
  | 'financial'
  | 'pos';

const TAB_KEYS: BranchTabKey[] = [
  'overview',
  'qr-menu',
  'daily-logins',
  'financial',
  'pos'
];

const BranchDetails = () => {
  const { branchId } = useParams<{ branchId: string }>();
  const { t } = useTranslation();
  const location = useLocation();
  const branchStateData = useObservable(branchState$);
  const [searchParams, setSearchParams] = useSearchParams();
  const { isSuperAdmin, isCompanyAdmin } = useUserViewMode();
  // The company page holds its tax setup: only admins of the company use it.
  const canOpenCompany = isSuperAdmin || isCompanyAdmin;

  // Find branch according to priority:
  // 1. location.state (most recent data passed via navigation)
  // 2. selected branch in store
  // 3. branchId in the list of all branches in store
  const findBranchFromStore = () => {
    const selectedBranch = branchStateData.selectedBranch.get();

    if (
      selectedBranch &&
      branchId &&
      selectedBranch.id === parseInt(branchId)
    ) {
      return selectedBranch;
    }

    if (branchId) {
      return (
        branchStateData.branches
          .get()
          .find((b) => b.id === parseInt(branchId)) || null
      );
    }

    return null;
  };

  const initialBranch = location.state?.branch || findBranchFromStore();

  const [branch, setBranch] = useState<Branch | null>(initialBranch);
  const [loading, setLoading] = useState<boolean>(!branch);
  const [previewModalOpen, setPreviewModalOpen] = useState<boolean>(false);
  const [previewImageUrl, setPreviewImageUrl] = useState<string>('');

  const currentTabKey = useMemo<BranchTabKey>(() => {
    const raw = searchParams.get('tab') as BranchTabKey | null;
    return raw && TAB_KEYS.includes(raw) ? raw : 'overview';
  }, [searchParams]);

  const currentTabIndex = TAB_KEYS.indexOf(currentTabKey);

  const handleTabChange = (_event: SyntheticEvent, newIndex: number) => {
    const nextKey = TAB_KEYS[newIndex];
    // Preserve any other query params if the app ever adds them, and keep
    // history clean (replace, not push) so browser Back returns to the list.
    const next = new URLSearchParams(searchParams);
    next.set('tab', nextKey);
    setSearchParams(next, { replace: true });
  };

  useEffect(() => {
    if (branch) {
      setSelectedBranch(branch);
    }
  }, [branch]);

  useEffect(() => {
    const fetchBranchDetails = async () => {
      if (!branchId) return;
      if (branch) return;

      setLoading(true);
      try {
        const result = await branchService.getById(Number(branchId));
        setBranch(result);
        setSelectedBranch(result);
      } catch (err) {
        console.error('Error fetching branch details:', err);
      } finally {
        setLoading(false);
      }
    };

    fetchBranchDetails();
  }, [branchId, branch, t]);

  const handleImageClick = (imageUrl: string) => {
    if (imageUrl) {
      setPreviewImageUrl(imageUrl);
      setPreviewModalOpen(true);
    }
  };

  const handleClosePreviewModal = () => {
    setPreviewModalOpen(false);
  };

  if (loading) {
    return (
      <Box
        display="flex"
        justifyContent="center"
        alignItems="center"
        height="80vh"
      >
        <CircularProgress />
      </Box>
    );
  }

  if (!branch) {
    return (
      <Container>
        <Typography variant="h4" gutterBottom color="error">
          {t('branch.not.found')}
        </Typography>
      </Container>
    );
  }

  const numericBranchId = parseInt(branchId || '0');

  return (
    <>
      <Helmet>
        <title>{t('branch.details')}</title>
      </Helmet>
      <PageTitleWrapper>
        <PageHeader />
      </PageTitleWrapper>

      <Container disableGutters maxWidth={false} sx={{ maxWidth: '90%' }}>
        <Paper elevation={3} sx={{ borderRadius: 2, overflow: 'hidden' }}>
          <Box sx={{ borderBottom: 1, borderColor: 'divider' }}>
            <Tabs
              value={currentTabIndex}
              onChange={handleTabChange}
              variant="scrollable"
              scrollButtons="auto"
              allowScrollButtonsMobile
              textColor="primary"
              indicatorColor="primary"
              aria-label={t('branch.details')}
            >
              <Tab
                icon={<InfoOutlinedIcon />}
                iconPosition="start"
                label={t('branch.tab.overview')}
                id="branch-tab-0"
                aria-controls="branch-tabpanel-0"
              />
              <Tab
                icon={<QrCode2Icon />}
                iconPosition="start"
                label={t('branch.tab.qrMenu')}
                id="branch-tab-1"
                aria-controls="branch-tabpanel-1"
              />
              <Tab
                icon={<EventAvailableIcon />}
                iconPosition="start"
                label={t('branch.tab.dailyLogins')}
                id="branch-tab-2"
                aria-controls="branch-tabpanel-2"
              />
              <Tab
                icon={<AccountBalanceIcon />}
                iconPosition="start"
                label={t('branch.tab.financial')}
                id="branch-tab-3"
                aria-controls="branch-tabpanel-3"
              />
              <Tab
                icon={<PointOfSaleIcon />}
                iconPosition="start"
                label={t('branch.tab.pos')}
                id="branch-tab-4"
                aria-controls="branch-tabpanel-4"
              />
            </Tabs>
          </Box>

          <Box sx={{ p: { xs: 2, sm: 3 } }}>
            <TabPanel
              value={currentTabIndex}
              index={0}
              id="branch-tabpanel-0"
              aria-labelledby="branch-tab-0"
            >
              <Grid container spacing={3}>
                <Grid item xs={12}>
                  <Grid container spacing={3}>
                    {branch?.file?.url && (
                      <Grid item xs={12} sm={4} md={3}>
                        <Box
                          sx={{
                            display: 'flex',
                            justifyContent: 'center',
                            alignItems: 'center',
                            border: '1px solid',
                            borderColor: 'divider',
                            borderRadius: 1,
                            p: 1,
                            backgroundColor: 'background.paper',
                            minHeight: '200px'
                          }}
                        >
                          <img
                            src={branch?.file?.url || ''}
                            alt={branch.name}
                            style={{
                              maxWidth: '100%',
                              maxHeight: '100%',
                              objectFit: 'contain',
                              borderRadius: '4px',
                              cursor: 'pointer'
                            }}
                            onClick={() =>
                              handleImageClick(branch?.file?.url || '')
                            }
                          />
                        </Box>
                      </Grid>
                    )}

                    <Grid
                      item
                      xs={12}
                      sm={branch?.file?.url ? 8 : 12}
                      md={branch?.file?.url ? 9 : 12}
                    >
                      <Grid container spacing={2}>
                        <Grid item xs={12} sm={6} md={4}>
                          <Typography
                            variant="subtitle2"
                            color="text.secondary"
                          >
                            {t('name')}
                          </Typography>
                          <Typography
                            variant="body1"
                            gutterBottom
                            fontWeight="bold"
                          >
                            {branch.name}
                          </Typography>
                        </Grid>

                        <Grid item xs={12} sm={6} md={4}>
                          <Typography
                            variant="subtitle2"
                            color="text.secondary"
                          >
                            {t('company')}
                          </Typography>
                          {canOpenCompany && branch.companyId ? (
                            <Link
                              component={RouterLink}
                              to={`/management/companies/${branch.companyId}`}
                              variant="body1"
                              gutterBottom
                              display="block"
                            >
                              {branch.company?.name || '-'}
                            </Link>
                          ) : (
                            <Typography variant="body1" gutterBottom>
                              {branch.company?.name || '-'}
                            </Typography>
                          )}
                        </Grid>

                        <Grid item xs={12} sm={6} md={4}>
                          <Typography
                            variant="subtitle2"
                            color="text.secondary"
                          >
                            {t('status')}
                          </Typography>
                          <StatusLabel status={branch.status as BranchStatus} />
                        </Grid>

                        <Grid item xs={12} sm={6} md={4}>
                          <Typography
                            variant="subtitle2"
                            color="text.secondary"
                          >
                            {t('created.at')}
                          </Typography>
                          <Typography variant="body1" gutterBottom>
                            {formatDateToDayMonthYearTime(branch.createdAt)}
                          </Typography>
                        </Grid>

                        <Grid item xs={12} sm={6} md={4}>
                          <Typography
                            variant="subtitle2"
                            color="text.secondary"
                          >
                            {t('updated.at')}
                          </Typography>
                          <Typography variant="body1" gutterBottom>
                            {branch.updatedAt
                              ? formatDateToDayMonthYearTime(branch.updatedAt)
                              : '-'}
                          </Typography>
                        </Grid>

                        {branch.mapcode && (
                          <Grid item xs={12} sm={6} md={4}>
                            <Typography
                              variant="subtitle2"
                              color="text.secondary"
                              sx={{ display: 'flex', alignItems: 'center' }}
                            >
                              <MapIcon fontSize="small" sx={{ mr: 0.5 }} />
                              {t('mapcode')}
                            </Typography>
                            <Typography variant="body1" gutterBottom>
                              {branch.mapcode}
                            </Typography>
                          </Grid>
                        )}
                      </Grid>
                    </Grid>
                  </Grid>
                </Grid>

                <Grid item xs={12}>
                  <Grid container spacing={2}>
                    <Grid item xs={12} md={4}>
                      <Card sx={{ height: '100%' }}>
                        <CardContent>
                          <Box display="flex" alignItems="center">
                            <PersonIcon
                              color="primary"
                              sx={{ fontSize: 40, mr: 2 }}
                            />
                            <Box>
                              <Typography variant="h3" fontWeight="bold">
                                {branch.numberOfUsersRegistered || 0}
                              </Typography>
                              <Typography
                                variant="subtitle2"
                                color="text.secondary"
                              >
                                {t('registered.users')}
                              </Typography>
                            </Box>
                          </Box>
                        </CardContent>
                      </Card>
                    </Grid>

                    <Grid item xs={12} md={4}>
                      <Card sx={{ height: '100%' }}>
                        <CardContent>
                          <Box display="flex" alignItems="center">
                            <ShoppingCartIcon
                              color="primary"
                              sx={{ fontSize: 40, mr: 2 }}
                            />
                            <Box>
                              <Typography variant="h3" fontWeight="bold">
                                {branch.numberOfProductsRegistered || 0}
                              </Typography>
                              <Typography
                                variant="subtitle2"
                                color="text.secondary"
                              >
                                {t('registered.products')}
                              </Typography>
                            </Box>
                          </Box>
                        </CardContent>
                      </Card>
                    </Grid>

                    <Grid item xs={12} md={4}>
                      <Card sx={{ height: '100%' }}>
                        <CardContent>
                          <Box
                            display="flex"
                            alignItems="center"
                            justifyContent="center"
                          >
                            <LocalOfferIcon
                              color="primary"
                              sx={{ fontSize: 40, mr: 2 }}
                            />
                            <Box>
                              <Typography variant="h6" fontWeight="bold">
                                {t('daily.login.types')}
                              </Typography>
                              <Typography
                                variant="subtitle2"
                                color="text.secondary"
                              >
                                {t('branch.rules')}
                              </Typography>
                            </Box>
                          </Box>
                        </CardContent>
                      </Card>
                    </Grid>
                  </Grid>
                </Grid>

                <Grid item xs={12} md={8}>
                  <BranchBalanceSettingsCard branchId={numericBranchId} />
                </Grid>

                {hasTaxSetupSupport(branch, 'taxIdentity') && (
                  <Grid item xs={12} md={8}>
                    <BranchTaxIdentityCard
                      branch={branch}
                      onSaved={setBranch}
                    />
                  </Grid>
                )}

                {branch.contact && (
                  <Grid item xs={12}>
                    <Typography variant="h5" gutterBottom>
                      {t('contact.information')}
                    </Typography>
                    <Divider sx={{ mb: 2 }} />

                    <Grid container spacing={3}>
                      {branch.contact.email && (
                        <Grid item xs={12} sm={6}>
                          <Box display="flex" alignItems="center">
                            <EmailIcon sx={{ mr: 1 }} color="action" />
                            <Typography variant="body1">
                              {branch.contact.email}
                            </Typography>
                          </Box>
                        </Grid>
                      )}

                      {branch.contact.phone && (
                        <Grid item xs={12} sm={6}>
                          <Box display="flex" alignItems="center">
                            <PhoneIcon sx={{ mr: 1 }} color="action" />
                            <Typography variant="body1">
                              {branch.contact.phone}
                            </Typography>
                          </Box>
                        </Grid>
                      )}
                    </Grid>
                  </Grid>
                )}
              </Grid>
            </TabPanel>

            <TabPanel
              value={currentTabIndex}
              index={1}
              id="branch-tabpanel-1"
              aria-labelledby="branch-tab-1"
            >
              <BranchQrMenuManager
                branchId={numericBranchId}
                branchName={branch.name}
                companyName={branch.company?.name}
              />
            </TabPanel>

            <TabPanel
              value={currentTabIndex}
              index={2}
              id="branch-tabpanel-2"
              aria-labelledby="branch-tab-2"
            >
              <BranchFeatureGate
                branchId={numericBranchId}
                featureKey={FeatureKey.DAILY_LOGINS}
                titleKey="licensing.daily_logins.gate.title"
                descriptionKey="licensing.daily_logins.gate.description"
                ctaKey="licensing.daily_logins.gate.cta"
              >
                <DailyLoginTypesManager
                  branchId={numericBranchId}
                  companyId={branch.companyId}
                />
              </BranchFeatureGate>
            </TabPanel>

            <TabPanel
              value={currentTabIndex}
              index={3}
              id="branch-tabpanel-3"
              aria-labelledby="branch-tab-3"
            >
              <BranchFeatureGate
                branchId={numericBranchId}
                featureKey={FeatureKey.FINANCE}
                titleKey="licensing.finance.gate.title"
                descriptionKey="licensing.finance.gate.description"
                ctaKey="licensing.finance.gate.cta"
              >
                <BranchFinancialInfoForm
                  branchId={numericBranchId}
                  taxSetupSupported={hasTaxSetupSupport(branch, 'taxIdentity')}
                />
              </BranchFeatureGate>
            </TabPanel>

            <TabPanel
              value={currentTabIndex}
              index={4}
              id="branch-tabpanel-4"
              aria-labelledby="branch-tab-4"
            >
              {/*
                Faz 12.2: POS ayarları ve işlem listesi FISCAL_POS lisansı
                olmayan şubelerde disabled + upgrade-CTA olarak gösterilir.
                Backend (Faz 12.1) her iki yüzeyi de zaten 403 ile korur;
                buradaki gate kullanılabilirlik ve satış hatırlatması katmanı.
              */}
              <BranchFeatureGate
                branchId={numericBranchId}
                featureKey={FeatureKey.FISCAL_POS}
                titleKey="licensing.fiscal_pos.gate.title"
                descriptionKey="licensing.fiscal_pos.gate.description"
                ctaKey="licensing.fiscal_pos.gate.cta"
              >
                <BranchPosSettingsManager branchId={numericBranchId} />
                {/*
                  POS İşlemleri: refunds, the Token / InPOS "Cihaz dışında
                  iade edildi" record and day end reconciliation. Every Token /
                  InPOS refund error in the apps points branch admins here.
                */}
                <BranchPosTransactionsManager branchId={numericBranchId} />
              </BranchFeatureGate>
            </TabPanel>
          </Box>
        </Paper>
      </Container>

      <ImagePreviewModal
        open={previewModalOpen}
        onClose={handleClosePreviewModal}
        imageUrl={previewImageUrl}
      />
    </>
  );
};

export default BranchDetails;
